import { Buffer } from "buffer";
import { ReportLog } from "./ReportLog";
import Report from "./Report";
import Utils from "./Utils";
import SaapApi from "./SaapApi";
import SdkInfo from "./SdkInfo";

class BleHandler {
  static instance = null;
  static mListener = null;
  static mFrameCounter;
  static mFrameKey = null;
  mHandshakeCode = null;

  static getInstance(reuse) {
    if (reuse) {
      if (BleHandler.instance === null) {
        BleHandler.instance = new BleHandler();
      }
      return BleHandler.instance;
    } else {
      if (BleHandler.instance !== null && BleHandler.instance.isConnected) {
        BleHandler.instance.disconnect();
      }
      BleHandler.instance = new BleHandler();
      return BleHandler.instance;
    }
  }

  constructor() {
    this.device = null;
    this.isConnected = false;
    this.serviceUUID = "1b8420d8-7148-482d-a88b-b4715976f77b";
    this.characteristicUUIDs = {
      C1: "a9efecf1-5493-4101-a144-3ba0f6b3175a", // Send Token
      C3: "1e96e241-8684-41e2-bb58-5c1b25597afa", // Secure exchange of command
      C4: "b126896a-b0af-4f26-b880-839a853e803e", // Data received from device
    };
  }

  onDisconnected = (event) => {
    this.isConnected = false;
    console.log(`OnDisconnect!! The device ${event.target} is disconnected`);
  };

  handleCharacteristicValueChanged = (event) => {
    const value = event.target.value; // DataView of the received data
    const receivedData = new Uint8Array(value.buffer);

    console.log("C4 Received data:", receivedData);

    let hex = Buffer.from(receivedData).toString("hex");
    console.log("C4 Received data hex:", hex);

    this.processReport(receivedData);

    // // Process the data (e.g., convert to string or parse bytes)
    // const textDecoder = new TextDecoder("utf-8");
    // console.log("Decoded data:", textDecoder.decode(receivedData));
  };

  /**
   * Process reports
   * @param {Uint8Array} data - The received data
   */
  processReport = (data) => {
    console.log("Process Received Data:", Utils.toHexString(data));

    const rep = new Report(this.mContext, this.mToken, this.mAction, data);

    // Send all notifications to application except report
    if (rep.notification !== SaapApi.NOTIF_NONE) {
      this.mListener.onNotification(rep.notification, "");

      if (rep.notification === SaapApi.NOTIF_LOCK_UNLOCKED) {
        const frameParams = new SdkInfo.FrameParams();
        frameParams.id = this.mToken.assetId;
        frameParams.counter = this.mFrameCounter;
        frameParams.key = this.mFrameKey;
        this.mSdkInfo.saveFrameParams(frameParams);
      }
    }

    const j = rep.log.json();
    this.mListener.onNotification(SaapApi.NOTIF_DEBUG, j);
    this.mListener.onNotification(
      SaapApi.NOTIF_SRV_REPORT,
      Utils.createJwt(j),
      j
    );

    // Track completion
    if (
      rep.responsePending === 0 &&
      rep.responseType === SaapApi.RESPONSE_COMMAND
    ) {
      this.mListener.onNotification(SaapApi.NOTIF_TRANS_COMPLETED, "");
    }
  };

  connectToDevice = async (deviceId) => {
    try {
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { name: "LOKR" }, // Filter by an exact name
          { manufacturerData: [{ companyIdentifier: 0x4c52 }] }, // Filter by manufacturer data
        ],
        //acceptAllDevices: true,
        //name: deviceId,
        optionalServices: [this.serviceUUID],
      });

      console.log("Device Info:", this.device);

      // Add an event listener to detect when a device disconnects
      this.device.addEventListener(
        "gattserverdisconnected",
        this.onDisconnected
      );

      // Try to connect to the remote GATT Server running on the Bluetooth device
      const server = await this.device.gatt.connect();
      console.log("Device Connected:", server, this.device.gatt.connected);
      this.isConnected = this.device.gatt.connected;

      // Get the service from the Bluetooth device
      const service = await server.getPrimaryService(this.serviceUUID);
      console.log("Service obtained:", service);

      this.characteristic1 = await service.getCharacteristic(
        this.characteristicUUIDs.C1
      );
      this.characteristic3 = await service.getCharacteristic(
        this.characteristicUUIDs.C3
      );
      this.characteristic4 = await service.getCharacteristic(
        this.characteristicUUIDs.C4
      );
      console.log("Characteristics obtained");

      // let characteristic = await service.getCharacteristic(
      //   "0000abcd-0000-1000-8000-00805f9b34fb"
      // );
      // if (characteristic) {
      //   let val = await characteristic.readValue();
      //   console.log("CHAR VALUE:", val);
      // } else {
      //   console.log("No CHAR to read BT ADDR");
      // }

      // Subscribe to response data (through C4)
      await this.characteristic4.startNotifications();
      // Setup listener for C4
      this.characteristic4.addEventListener(
        "characteristicvaluechanged",
        this.handleCharacteristicValueChanged
      );
    } catch (error) {
      console.log(`There was an error in BT connection: ${error}`);
    }
  };

  async disconnect() {
    if (this.device) {
      this.device.gatt.disconnect();
    } else {
      this.isConnected = false;
    }
  }

  monitorCharacteristic = (characteristicUUID, callback) => {
    if (!this.isConnected || !this.device)
      throw new Error("Device not connected");
    this.device.monitorCharacteristicForService(
      this.serviceUUID,
      characteristicUUID,
      (error, characteristic) => {
        if (error) {
          console.error("Monitor error:", error);
          return;
        }
        const data = Buffer.from(characteristic.value, "base64").toString(
          "hex"
        );
        callback(data);
      }
    );
  };

  requestMtu = async (mtuSize) => {
    if (!this.isConnected || !this.device)
      throw new Error("Device not connected");
    const mtu = await this.device.requestMTU(mtuSize);
    return mtu;
  };

  readRssi = async () => {
    if (!this.isConnected || !this.device)
      throw new Error("Device not connected");
    const rssi = await this.device.readRSSI();
    return rssi;
  };

  handshakeCode = () => {
    if (this.mHandshakeCode == null) {
      this.mHandshakeCode = [
        Math.floor(Math.random() * 256),
        Math.floor(Math.random() * 256),
      ];
      console.log("Hadshake code", this.mHandshakeCode);
    }
    return this.mHandshakeCode;
  };

  syncTime = () => {
    let utc = Math.floor(new Date().getTime() / 1000);
    const byteArray = new Uint8Array(4); // Assuming 4-byte integer

    for (let i = 0; i < 4; i++) {
      byteArray[i] = utc & 0xff;
      utc >>= 8;
    }

    return byteArray;
  };

  frameMessage = async (token, action, sdkInfo) => {
    const handshake = this.handshakeCode();
    const utc = this.syncTime();
    const idx = 0; // Math.floor(Math.random() * 10);
    var sdkcode = Buffer.from(sdkInfo.sdkId, "base64");

    console.log("SDK Code:", sdkInfo.sdkId);
    console.log("SDK Code Bytes:", sdkcode);

    const data = new Uint8Array([
      action, // 1 byte action
      handshake[0], // 2 bytes handshake
      handshake[1],
      utc[0], // 4 bytes sync time
      utc[1],
      utc[2],
      utc[3],
      sdkcode[idx], // 7 bytes of sdkcode starting from random index
      sdkcode[idx + 1],
      sdkcode[idx + 2],
      sdkcode[idx + 3],
      sdkcode[idx + 4],
      sdkcode[idx + 5],
      sdkcode[idx + 6],
      0, // Frame counter
      idx, // random index between 0-10
    ]);

    console.log("FRAMEDATA:", Utils.toHexString(data));

    if (this.mFrameKey == null) {
      this.mFrameKey = await Utils.createKey(
        sdkcode,
        token.assetId,
        token.tokenData
      );

      console.log("FRAMEKEY:", this.mFrameKey);
    }
    return Utils.encryptMsg(data, this.mFrameKey);
  };

  async writeData(data, len) {
    let i = 0;
    while (i < len) {
      let part;
      let l;

      l = len - i > 64 ? 64 : len - i;

      part = data.slice(i, l);
      i += l;

      await this.characteristic1.writeValue(part);
    }
  }

  processToken = async (
    ctx,
    assetId,
    token,
    isConfig,
    action,
    sdkInfo,
    listener
  ) => {
    console.log("BLE Process Token", token.tokenData);
    console.log("SDK Info", sdkInfo);
    console.log("SDK Action", action);

    this.mAssetId = assetId;
    this.mContext = ctx;
    this.mToken = token;
    this.mAction = action;
    this.mSdkInfo = sdkInfo;
    this.mListener = listener;
    this.mIsConfig = isConfig;

    this.reportLog = new ReportLog(this.mToken);

    await this.connectToDevice(token.btAddr);

    // console.log("Disconecting..");
    // await this.disconnect();
    // return;

    //let data3 = await this.frameMessage(token, action, sdkInfo);
    // console.log("DATA3: ", data3);
    // return;

    // Send token
    const encoder = new TextEncoder();
    const encodedString = encoder.encode(token.tokenData); // .buffer
    console.log("TOKEN DATA:", encodedString);
    // console.log("Encoded string", encodedString.toString("hex"));

    // let data = new Uint8Array([
    //   101, 121, 74, 104, 73, 106, 111, 121, 76, 67, 74, 105, 73, 106, 111, 48,
    //   102, 81, 46, 101,
    // ]);
    // await this.characteristic1.writeValue(data);
    // encodedString.slice(0, 64)
    await this.characteristic1.writeValue(encodedString);
    console.log("Sent token data");

    let data3 = await this.frameMessage(token, action, sdkInfo);
    console.log("DATA3: ", data3);

    await this.characteristic3.writeValue(data3); // .buffer
    console.log("Sent command");
  };
}

export default BleHandler;
